import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ActivityIndicator, FlatList, Platform, Pressable, TextInput, View } from 'react-native';
import { KeyboardAvoidingView } from 'react-native-keyboard-controller';
import { KEYBOARD_BAR_HEIGHT } from '@/components/keyboard/keyboard-bar';

import Animated, {
  FadeIn,
  FadeInDown,
  FadeOut,
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withRepeat,
  withTiming,
} from 'react-native-reanimated';

import { MarkdownText } from '@/components/chat/markdown-text';
import { ThemedText } from '@/components/themed-text';
import { useThemedHex } from '@/hooks/use-theme-colors';

/**
 * O cartão que pode acompanhar uma bolha. Genérico de propósito: a superfície
 * não conhece os tipos de widget — quem sabe desenhar é o `renderWidget` de
 * quem monta o chat (na entrevista, o `WidgetRenderer` do T-23).
 */
export interface ChatSurfaceWidget {
  type: string;
  data: Record<string, unknown>;
}

export interface ChatSurfaceMessage {
  id: string;
  body: string;
  /** Bolha da direita (quem está vendo) ou da esquerda. */
  mine: boolean;
  widget?: ChatSurfaceWidget | null;
}

export interface ChatSurfaceProps {
  messages: ChatSurfaceMessage[];
  onSend: (text: string) => void;
  /** Bloqueia o envio enquanto a mensagem anterior está em voo. */
  sending?: boolean;
  /** Três pontinhos: o outro lado está escrevendo. */
  isTyping?: boolean;
  isLoading?: boolean;
  /** Conversa encerrada: o campo some em vez de mentir que aceita resposta. */
  disabled?: boolean;
  brandColor?: string;
  placeholder?: string;
  emptyLabel?: string;
  /**
   * Desenha o cartão abaixo da bolha. Tipo desconhecido devolve `null`.
   *
   * `editable` vem pronto daqui — é sempre "o cartão do último turno, e só
   * enquanto nada está em voo". Calcular isso lá fora obrigava quem monta o
   * chat a refazer o `renderWidget` a cada tecla, e aí a lista inteira
   * (endereço, grade, lista de serviços) re-renderizava a cada envio.
   */
  renderWidget?: (
    widget: ChatSurfaceWidget,
    message: ChatSurfaceMessage,
    editable: boolean,
  ) => React.ReactNode;
  /** Faixa fixa entre a lista e o campo de texto (ex.: "prefiro configurar sozinho"). */
  footer?: React.ReactNode;
}

/**
 * A superfície de chat do app: bolhas, campo de texto, autoscroll e teclado.
 *
 * Não sabe de onde vêm as mensagens nem para onde vão — quem liga em hook,
 * realtime ou agente é quem usa. É o que permite o chat de recorrência (entre
 * duas pessoas) e a entrevista de onboarding (com o agente) dividirem a mesma
 * superfície sem um saber do outro.
 */
export function ChatSurface({
  messages,
  onSend,
  sending = false,
  isTyping = false,
  isLoading = false,
  disabled = false,
  brandColor,
  placeholder = 'Escreva uma mensagem',
  emptyLabel,
  renderWidget,
  footer,
}: ChatSurfaceProps) {
  const th = useThemedHex();
  const [text, setText] = useState('');
  const listRef = useRef<FlatList<ChatSurfaceMessage>>(null);

  /**
   * Onde a conversa começa na tela, medido — não chutado.
   *
   * O desvio do teclado compara a posição da conversa *dentro do pai* com a
   * altura da tela; a diferença (área segura + cabeçalho, que muda de aparelho
   * para aparelho) entra aqui. Com um número fixo, no iPhone com notch o campo
   * de texto ficava pela metade atrás do teclado. Soma a barra
   * anterior/próximo/concluir do iOS, que fica entre o teclado e o campo.
   */
  const surfaceRef = useRef<View>(null);
  const [surfaceTop, setSurfaceTop] = useState(0);
  const measureSurface = useCallback(() => {
    surfaceRef.current?.measureInWindow((_x, y) => {
      if (Number.isFinite(y)) setSurfaceTop(y);
    });
  }, []);

  const accent = brandColor || '#2563eb';

  /**
   * O chat só acompanha o fim enquanto ele **está** no fim.
   *
   * Quem rolou para cima para reler está lendo: puxar a tela de volta a cada
   * mensagem seria arrancar o texto da mão dele.
   */
  const stick = useRef(true);
  /**
   * Uma rolagem automática em andamento não pode ser lida como "ele saiu do
   * fim": no meio da animação a distância ainda é grande, e sem esta trava o
   * próprio movimento desligava o acompanhamento.
   */
  const auto = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** A altura real do conteúdo, medida pela própria lista. */
  const contentHeight = useRef(0);

  /**
   * A rolagem só é animada quando a fala é do outro lado.
   *
   * A própria mensagem tem que estar na tela no instante do Enter: 300ms de
   * rolagem suave entre digitar e ver a bolha é exatamente o "demorou" que o
   * usuário sente, mesmo com a bolha já montada.
   */
  const animateScroll = useRef(true);

  const scrollToEnd = useCallback(() => {
    // Dois quadros: o primeiro deixa o React montar a bolha nova, o segundo
    // mede o cartão que veio dentro dela. Com um só, a lista rola para a
    // altura que existia antes do widget — que é o "sempre fica bem acima".
    if (auto.current) clearTimeout(auto.current);
    auto.current = setTimeout(() => {
      auto.current = null;
    }, 500);

    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        // `scrollToEnd` erra o alvo quando o conteúdo cresceu no mesmo quadro
        // (é o caso dos pontinhos e dos cartões): ele usa a altura que a lista
        // tinha antes. Rolar para a altura medida, com folga, é determinístico
        // — o excesso é aparado pela própria lista.
        const animated = animateScroll.current;
        const offset = contentHeight.current;
        if (offset > 0) {
          listRef.current?.scrollToOffset({ offset, animated });
          return;
        }
        listRef.current?.scrollToEnd({ animated });
      }),
    );
  }, []);

  const lastIsMine = messages[messages.length - 1]?.mine ?? false;

  useEffect(() => {
    if (messages.length === 0) return;
    // Mensagem nova é sempre para ser vista: volta a colar no fim.
    stick.current = true;
    animateScroll.current = !lastIsMine;
    scrollToEnd();
  }, [messages.length, lastIsMine, scrollToEnd]);

  useEffect(() => {
    if (!isTyping) return;
    stick.current = true;
    animateScroll.current = true;
    scrollToEnd();
  }, [isTyping, scrollToEnd]);

  const handleSend = () => {
    const body = text.trim();
    if (!body || sending || disabled) return;
    setText('');
    onSend(body);
  };

  /**
   * No teclado físico, Enter envia e Shift+Enter pula linha — o que qualquer
   * chat faz, e o que a mão do usuário já espera.
   *
   * Só na web: no celular o Enter do teclado virtual é quebra de linha, e quem
   * envia é o botão. O `preventDefault` faz duas coisas necessárias — impede a
   * quebra de linha e faz o react-native-web pular o `onSubmitEditing` dele,
   * que borraria o campo a cada mensagem.
   *
   * `isComposing` protege o acento morto: em teclado ABNT, digitar "ç" ou "ã"
   * passa por uma composição que termina em Enter, e enviar ali cortaria a
   * palavra no meio.
   */
  const handleKeyPress = (event: unknown) => {
    if (Platform.OS !== 'web') return;
    const key = event as {
      key?: string;
      shiftKey?: boolean;
      preventDefault?: () => void;
      nativeEvent?: { isComposing?: boolean; keyCode?: number };
    };
    if (key.key !== 'Enter' || key.shiftKey) return;
    if (key.nativeEvent?.isComposing || key.nativeEvent?.keyCode === 229) return;

    key.preventDefault?.();
    handleSend();
  };

  if (isLoading) {
    return (
      <View style={{ flex: 1, justifyContent: 'center' }}>
        <ActivityIndicator />
      </View>
    );
  }

  return (
    <View ref={surfaceRef} style={{ flex: 1 }} onLayout={measureSurface}>
      <KeyboardAvoidingView
        style={{ flex: 1 }}
        behavior="padding"
        keyboardVerticalOffset={surfaceTop + KEYBOARD_BAR_HEIGHT}
      >
        <FlatList
          ref={listRef}
          data={messages}
          keyExtractor={(m) => m.id}
          // Folga no fim: sem ela os pontinhos, que entram depois de tudo
          // medido, encostam na borda e aparecem cortados ao meio.
          contentContainerStyle={{
            paddingHorizontal: 16,
            paddingTop: 16,
            paddingBottom: 96,
            gap: 8,
          }}
          keyboardShouldPersistTaps="handled"
          // Uma conversa não tem mil linhas, e a bolha nova nasce **abaixo** do
          // que está na tela — ou seja, fora da janela virtual. Com os padrões
          // da lista ela esperava o próximo lote (`updateCellsBatchingPeriod`)
          // para existir: era o envio que parecia lento. Aqui ela monta no
          // mesmo quadro.
          initialNumToRender={20}
          maxToRenderPerBatch={40}
          updateCellsBatchingPeriod={0}
          windowSize={21}
          removeClippedSubviews={false}
          renderItem={({ item, index }) => (
            <ChatRow
              message={item}
              accent={accent}
              renderWidget={renderWidget}
              // Só o cartão do último turno aceita toque, e só enquanto nenhuma
              // resposta está em voo. Vindo pronto como booleano, a bolha antiga
              // recebe sempre `false` e o `memo` a segura fora do re-render.
              editable={index === messages.length - 1 && !sending}
            />
          )}
          // O cartão cresce depois de montado (o sheet abre, o CEP responde, a
          // lista de serviços muda de tamanho). Sem isto, a rolagem para na
          // altura de antes e a pergunta fica escondida embaixo.
          ListFooterComponent={isTyping ? <TypingBubble /> : null}
          // O teclado abriu (ou fechou) e a lista encolheu: quem estava no fim
          // continua vendo a última fala, não o meio da conversa.
          onLayout={() => {
            if (stick.current) scrollToEnd();
          }}
          onContentSizeChange={(_width, height) => {
            contentHeight.current = height;
            if (stick.current) scrollToEnd();
          }}
          onScroll={(event) => {
            if (auto.current) return;
            const { contentOffset, contentSize, layoutMeasurement } = event.nativeEvent;
            const distance = contentSize.height - (contentOffset.y + layoutMeasurement.height);
            stick.current = distance < 96;
          }}
          scrollEventThrottle={64}
          ListEmptyComponent={
            emptyLabel ? (
              <ThemedText style={{ textAlign: 'center', opacity: 0.6, marginTop: 24 }}>
                {emptyLabel}
              </ThemedText>
            ) : null
          }
        />

        {footer}

        {!disabled && (
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: 8,
              padding: 12,
              borderTopWidth: 1,
              borderTopColor: th('#e5e7eb'),
            }}
          >
            <TextInput
              value={text}
              onChangeText={setText}
              onKeyPress={handleKeyPress}
              placeholder={placeholder}
              // Ao tocar no campo, a conversa sobe junto com o teclado — sem
              // animação: a lista encolhe quadro a quadro, e uma rolagem suave
              // por quadro tremeria.
              onFocus={() => {
                stick.current = true;
                animateScroll.current = false;
              }}
              style={{
                flex: 1,
                // Texto longo rola dentro do campo em vez de empurrar a conversa
                // para trás do cabeçalho.
                maxHeight: 120,
                backgroundColor: th('#f3f4f6'),
                borderRadius: 24,
                paddingHorizontal: 16,
                paddingVertical: 10,
              }}
              multiline
            />
            <Pressable
              onPress={handleSend}
              disabled={sending || !text.trim()}
              style={{
                backgroundColor: accent,
                borderRadius: 24,
                paddingHorizontal: 18,
                paddingVertical: 10,
                opacity: sending || !text.trim() ? 0.5 : 1,
              }}
            >
              <ThemedText style={{ color: '#fff', fontWeight: '600' }}>Enviar</ThemedText>
            </Pressable>
          </View>
        )}
      </KeyboardAvoidingView>
    </View>
  );
}

/**
 * Uma bolha da lista.
 *
 * `memo` porque o custo real do chat está nos cartões: endereço com CEP, grade
 * semanal, lista de serviços. Sem isto, cada tecla e cada envio re-renderizava
 * todos os cartões já respondidos — era esse trabalho, e não a rede, que
 * atrasava a própria fala do usuário a aparecer na tela.
 */
const ChatRow = React.memo(function ChatRow({
  message,
  accent,
  editable,
  renderWidget,
}: {
  message: ChatSurfaceMessage;
  accent: string;
  editable: boolean;
  renderWidget?: (
    widget: ChatSurfaceWidget,
    message: ChatSurfaceMessage,
    editable: boolean,
  ) => React.ReactNode;
}) {
  const th = useThemedHex();
  const widget = message.widget ? renderWidget?.(message.widget, message, editable) : null;

  // A **própria** fala não anima na entrada.
  //
  // A animação de entrada do Reanimated monta o elemento invisível e só o
  // revela quando o driver começa — e dentro de uma lista virtualizada isso
  // não é no quadro do toque. Para a fala do outro lado ninguém percebe (já
  // se esperou a resposta); para a que o usuário acabou de escrever, é
  // exatamente o atraso entre apertar Enter e ver a mensagem. Ela aparece
  // pronta, no quadro do toque.
  return (
    // A bolha do outro lado entra subindo, como em qualquer chat.
    <Animated.View
      entering={message.mine ? undefined : FadeInDown.duration(160)}
      style={{ gap: 8 }}
    >
      <View
        style={{
          alignSelf: message.mine ? 'flex-end' : 'flex-start',
          backgroundColor: message.mine ? accent : th('#e5e7eb'),
          borderRadius: 16,
          paddingHorizontal: 14,
          paddingVertical: 9,
          maxWidth: '80%',
        }}
      >
        {message.mine ? (
          // A própria fala é texto cru: o usuário não escreve markdown, e
          // interpretar `*` dele apagaria o asterisco que ele quis mandar.
          <ThemedText style={{ color: '#fff' }}>{message.body}</ThemedText>
        ) : (
          <MarkdownText text={message.body} color={th('#111827')} />
        )}
      </View>
      {widget ? (
        // O cartão entra depois da bolha: primeiro a fala, depois o que ela
        // mostra.
        <Animated.View entering={FadeInDown.delay(90).duration(240)}>{widget}</Animated.View>
      ) : null}
    </Animated.View>
  );
});

/**
 * Três pontinhos pulsando, na mesma bolha cinza das falas do outro lado.
 *
 * Estáticos, os pontos pareciam uma mensagem travada — o indicador só cumpre o
 * papel dele (dizer "estou respondendo, espera") quando se mexe. A animação é
 * por `style`, e não por `className`: componente animado com NativeWind exige
 * `cssInterop`, e aqui não vale a dependência por três bolinhas.
 */
function TypingBubble() {
  const th = useThemedHex();
  return (
    <Animated.View
      entering={FadeIn.duration(160)}
      exiting={FadeOut.duration(120)}
      style={{
        alignSelf: 'flex-start',
        flexDirection: 'row',
        gap: 4,
        alignItems: 'center',
        backgroundColor: th('#e5e7eb'),
        borderRadius: 16,
        paddingHorizontal: 14,
        paddingVertical: 11,
        marginTop: 8,
      }}
    >
      {[0, 1, 2].map((index) => (
        <TypingDot key={index} delay={index * 160} />
      ))}
    </Animated.View>
  );
}

/** Um ponto: sobe e clareia, com atraso próprio para virar onda. */
function TypingDot({ delay }: { delay: number }) {
  const th = useThemedHex();
  const progress = useSharedValue(0);

  useEffect(() => {
    progress.value = withDelay(delay, withRepeat(withTiming(1, { duration: 520 }), -1, true));
  }, [delay, progress]);

  const style = useAnimatedStyle(() => ({
    opacity: 0.35 + progress.value * 0.65,
    transform: [{ translateY: -progress.value * 3 }],
  }));

  return (
    <Animated.View
      style={[
        {
          width: 6,
          height: 6,
          borderRadius: 3,
          backgroundColor: th('#6b7280'),
        },
        style,
      ]}
    />
  );
}
